import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { allArticles, articleCategories } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = useMemo(() => allArticles.find((a) => a.id === slug), [slug]);

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return allArticles
      .filter((a) => a.category === article.category && a.id !== article.id)
      .slice(0, 3);
  }, [article]);

  const categoryInfo = useMemo(
    () => articleCategories.find((c) => c.slug === article?.category),
    [article],
  );

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center pt-16">
          <div className="text-center">
            <span className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-background-100 text-foreground-400">
              <i className="ri-file-unknow-line text-2xl"></i>
            </span>
            <h1 className="font-heading font-bold text-xl text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">您访问的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.imageUrl,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: '2026世界杯官网 ZL5.net',
      url: 'http://cefotiam.com/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://cefotiam.com/article/${article.id}`,
    },
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://cefotiam.com/' },
      { '@type': 'ListItem', position: 2, name: article.categoryLabel, item: 'http://cefotiam.com/category/all' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `http://cefotiam.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbJsonLd) }}
        />

        <article>
          <header className="pt-24 md:pt-28 pb-10 md:pb-14 bg-background-100">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <nav aria-label="breadcrumb" className="mb-6">
                <ol className="flex items-center gap-1.5 text-sm flex-wrap">
                  <li>
                    <Link to="/" className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                      首页
                    </Link>
                  </li>
                  <li>
                    <span className="text-foreground-400 mx-1">/</span>
                  </li>
                  <li>
                    <Link
                      to="/category/all"
                      className="text-foreground-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {article.categoryLabel}
                    </Link>
                  </li>
                  <li>
                    <span className="text-foreground-400 mx-1">/</span>
                  </li>
                  <li>
                    <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
                  </li>
                </ol>
              </nav>

              <div className="mb-6">
                <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-semibold mb-4">
                  {article.categoryLabel}
                </span>
                <h1 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line text-xs"></i>
                    </span>
                    {article.author}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-time-line text-xs"></i>
                    </span>
                    {article.date}
                  </div>
                </div>
              </div>

              <div className="rounded-xl overflow-hidden">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-full h-auto object-cover aspect-[2/1]"
                />
              </div>
            </div>
          </header>

          <section className="py-10 md:py-14 bg-background-50">
            <div className="max-w-[900px] mx-auto px-4 md:px-6">
              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg md:prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5 prose-p:text-base
                  prose-strong:text-foreground-900 prose-strong:font-semibold"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200">
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-background-100 text-foreground-600 rounded-full text-xs font-medium"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-16 md:py-20 bg-background-100">
            <div className="max-w-[1440px] mx-auto px-4 md:px-6">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 mb-8 md:mb-10">
                相关文章
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/article/${rel.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden transition-all duration-300 hover:border-primary-300/50 cursor-pointer"
                  >
                    <div className="relative overflow-hidden h-44">
                      <img
                        src={rel.imageUrl}
                        alt={rel.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors mb-2">
                        {rel.title}
                      </h3>
                      <p className="text-xs text-foreground-500">{rel.date}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}